import { Avatar, Box, Typography, IconButton, AppBar, useTheme, Popover, Select, MenuItem, Popper, ClickAwayListener, Card, Divider, Tooltip, Button } from "@mui/material"
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useEffect, useState } from "react"
import { observer } from 'mobx-react'

import { Icons } from '../../../../shared/utils/icons.jsx'
import { ERPDashboardPageURL } from '../../../../shared/utils/constants.jsx'
import { formatDate, secondsToHMS } from '../../../../shared/utils/date-time-utilities.jsx'
import { convertToCDNLink, getNameInitials } from '../../../../shared/utils/helpers.jsx'
import SimbaAIForStudents from '../../ai-mentor/SimbaAIForStudents'
import { useLayoutContext } from "../../../../shared/contexts/layout.context"
import { useThemeContext } from "../../../../shared/contexts/theme.context"
import { useUserStore } from "../../../store/user.store"
import aliveLogo from '../../../../shared/assets/alivelogo.png'
import simbaImg from '../../../../shared/assets/Habba-Logo.png'
import StudentPushNotifications from '../../../../notifications/StudentPushNotifications.jsx'
import placementTrackerAPI from '../../../../alive-placements/services/api/PlacementTrackerAPI.js'

const themeOptions = [
    { value: 'light', icon: Icons.default.LightMode, label: 'light' },
    { value: 'dark', icon: Icons.default.DarkMode, label: 'dark' },
    { value: 'system', icon: Icons.default.SystemTheme, label: 'system' },
]


export const EcosystemPortalCard = () => {
    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'

    return (
        <Box
            component={Link}
            to="/placements"
            target="_placements"
            className="default-link"
            sx={{ textDecoration: 'none', display: { xs: 'block', sm: "none" }, position: "sticky", bottom: 0, margin: "5px" }}
        >
            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    overflow: 'hidden',
                    borderRadius: 2.5,
                    p: 1,
                    cursor: 'pointer',
                    width: '100%',

                    background: isDark
                        ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[900]} 100%)`
                        : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.secondary.light} 100%)`,

                    color: theme.palette.text.primary,
                    border: '1px solid',
                    borderColor: isDark
                        ? 'rgba(255, 255, 255, 0.12)'
                        : 'rgba(0, 0, 0, 0.08)',

                    boxShadow: isDark
                        ? '0 4px 20px rgba(0, 0, 0, 0.4)'
                        : '0 4px 20px rgba(0, 0, 0, 0.05)',

                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

                    '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: theme.palette.secondary.main,
                        boxShadow: isDark
                            ? `0 8px 25px ${theme.palette.secondary.main}33`
                            : `0 8px 25px ${theme.palette.secondary.main}20`,
                        '& .arrow-icon': {
                            transform: 'translate(3px, -3px)',
                            color: theme.palette.secondary.main,
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.action.hover,
                        },
                        '& .bg-glow': {
                            opacity: isDark ? 0.25 : 0.4,
                            transform: 'scale(1.2)',
                        },
                    },
                }}
            >
                {/* Background Glow */}
                <Box
                    className="bg-glow"
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        background: theme.palette.secondary.main,
                        opacity: isDark ? 0.12 : 0.15,
                        filter: 'blur(25px)',
                        transition: 'all 0.5s ease',
                        pointerEvents: 'none',
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {/* Icon Container */}
                        <Box
                            sx={{
                                p: 1.2,
                                borderRadius: 2,
                                bgcolor: isDark
                                    ? 'rgba(255, 255, 255, 0.06)'
                                    : 'rgba(0, 0, 0, 0.04)',
                                color: theme.palette.secondary.main,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {Icons.default.Building1}
                        </Box>

                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.8,
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        color: theme.palette.secondary.main
                                    }}
                                >
                                    New Space
                                </Typography>
                            </Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    color: theme.palette.text.primary
                                }}
                            >
                                Placement Training
                            </Typography>
                        </Box>
                    </Box>

                    {/* Action Icon */}
                    <Box
                        className="arrow-icon"
                        sx={{
                            display: 'inline-flex',
                            p: 0.8,
                            borderRadius: '50%',
                            color: theme.palette.text.secondary,
                            bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {Icons.default.ArrowOutward}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

const SessionEndCountdown = () => {
    const [timeLeft, setTimeLeft] = useState( null )

    const navigate = useNavigate()

    useEffect( () => {
        const calculateTimeLeft = () => {
            const endsAt = sessionStorage.getItem( 'impersonation_session_ends_at' )

            if ( !endsAt ) return null

            // Convert stored value to a date (handles both ISO strings and timestamps)
            const difference = new Date( endsAt ) - new Date()

            if ( difference <= 0 ) {
                sessionStorage.removeItem( 'impersonation_id_token' )
                sessionStorage.removeItem( 'is_impersonation' )
                sessionStorage.removeItem( 'impersonator_details' )
                sessionStorage.removeItem( 'impersonation_session_ends_at' )
                return window.location.href = '/admin/impersonation'
            }

            return difference
        }

        // Initial check
        setTimeLeft( calculateTimeLeft() )

        // Update every second
        const timer = setInterval( () => {
            const remaining = calculateTimeLeft()
            setTimeLeft( remaining )

            if ( remaining === 0 ) {
                clearInterval( timer )
                // Optional: Force a logout or refresh when timer hits zero
                // window.location.reload(); 
            }
        }, 1000 )

        return () => clearInterval( timer )
    }, [] )

    return (
        <Typography
            variant="caption"
            sx={{
                fontSize: "10px",
                display: "block",
                fontWeight: 'bold',
                color: timeLeft < 60000 ? 'error.main' : 'inherit'
            }}>
            <Typography
                component="span"
                sx={{
                    display: { md: "inline", xs: "none" },
                    fontSize: "inherit",
                    fontWeight: "inherit",
                    color: 'inherit'
                }}>Ends In:</Typography> {secondsToHMS( timeLeft / 1000 )}
        </Typography>
    )
}

const Navbar = observer( ( { openSideMenu, closeSideMenu } ) => {
    const [settingsAnchorEl, setSettingsAnchorEl] = useState( null )
    const [profile, setProfile] = useState( null )

    const searchParamsObject = useSearchParams( { simbaAIOpen: 'false' } )
    const setSearchParams = searchParamsObject[1]

    const UserStore = useUserStore()
    const isImpersonation = sessionStorage.getItem( "is_impersonation" ) === 'true'

    const { navHeight, navHeadText } = useLayoutContext()

    const { palette, border, shadows } = useTheme()
    const { setSelectedTheme, selectedTheme } = useThemeContext()

    const enterSimbaAI = () => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            newParams.set( 'simbaAIOpen', 'true' )
            newParams.set( 'simbaAIMode', 'practice' )
            return newParams
        }, { replace: true } )
    }

    const handleThemeChange = value => {
        setSelectedTheme( value )
        localStorage.setItem( 'preferedTheme', value )
        setSettingsAnchorEl( null )
    }
    useEffect( () => {
        setProfile( UserStore.getUser.user_profile )
    }, [UserStore] )

    return (
        <AppBar
            id="appbar"
            elevation={0}
            sx={{
                borderRadius: "0",
                background: "transparent",
                transition: "0.2s ease-out",
                position: "static",
                color: palette.nav.font,
                height: { sm: "50px", xs: "60px" },
                zIndex: "200"
            }}
        >

            <SimbaAIForStudents />

            <Box
                id="app-bar"
                sx={{
                    alignItems: "center",
                    justifyContent: " space-between",
                    height: `${navHeight}px`,
                    padding: { sm: "0 20px 0 0", xs: "0 10px" },
                    display: "flex",
                    maxWidth: "100%",
                    transition: "0.2s ease-out"
                }}>
                <Box
                    sx={{
                        display: "flex",
                        gap: "10px",
                        alignItems: "center"
                    }}>
                    <Box
                        sx={{
                            padding: "2px",
                            display: { sm: "none", xs: "flex" },
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "5px",
                            bgcolor: "white",
                            aspectRatio: "1 / 1"
                        }}><Box component="img" src={aliveLogo} alt="Logo" sx={{
                            width: "35px"
                        }} /></Box>
                    <Box
                        component="img"
                        src={simbaImg}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            width: "20px"
                        }} />
                    <Typography
                        component='h1'
                        color={palette.nav.font}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            fontSize: "14px"
                        }}>
                        Acharya LMS
                    </Typography>
                    <Typography
                        component='span'
                        color={palette.nav.font}
                        sx={{
                            display: { sm: "block", xs: "none" },
                            fontSize: "14px"
                        }}>
                        |
                    </Typography>
                    <Typography color={palette.nav.font} component="span" sx={{
                        fontSize: { md: "14px", xs: "14px" }
                    }} >{navHeadText || `Welcome to digital classrooms`}</Typography>
                </Box>

                {<Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                    {isImpersonation && <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            bgcolor: palette.customThemeColor.light + "22",
                            minHeight: "15px",
                            gap: { md: "10px", xs: "2px" },
                            alignItems: "center",
                            border: `1px solid ${palette.customThemeColor.main}`,
                            padding: { md: "2px 10px", xs: "5px 10px" },
                            borderRadius: "20px"
                        }}>
                        {Icons.default.Impersonation}
                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    display: { md: "block", xs: "none" },
                                    fontSize: "12px"
                                }}>Impersonating</Typography>
                            <SessionEndCountdown />
                        </Box>
                    </Box>}
                    {UserStore.getUser.features_permissions['simba_ai'] && <Box
                        onClick={enterSimbaAI}
                        sx={{
                            display: "flex",
                            gap: "10px",
                            padding: "5px 10px",
                            alignItems: "center",
                            borderRadius: "20px",
                            boxShadow: shadows[1],
                            bgcolor: palette.customThemeColor.light + "99",
                            border: `2px solid ${palette.customThemeColor.main}`,
                            cursor: "pointer",
                            ":hover": { bgcolor: palette.customThemeColor.light + "dd" }
                        }}>
                        <Box
                            component="img"
                            src={simbaImg}
                            sx={{
                                width: "25px",
                                borderRadius: "50%",
                                height: "25px",
                                border: `2px solid ${palette.common.white}`
                            }} />
                        <Box>
                            {/* <Typography width="fit-content" padding="1px 5px" fontSize="8px" sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, padding: "3px", borderRadius: "5px" }}>NEW</Typography> */}
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: palette.text.primary
                                }}>Simba AI</Typography>
                        </Box>
                    </Box>}
                    <Box sx={{
                        display: { md: "block", xs: "none" }
                    }}>
                        <Typography
                            component="span"
                            sx={{
                                fontSize: "14px",
                                textAlign: "right",
                                textTransform: "capitalize",
                                color: palette.text.primary
                            }}>{formatDate( new Date(), { format: "LLL" } )}</Typography>
                    </Box>
                    {/* <IconButton onClick={( e ) => setSettingsAnchorEl( e.currentTarget )}>
                        {Icons.default.SettingsOutlined}
                    </IconButton> */}

                    <StudentPushNotifications
                        apiClient={placementTrackerAPI.apiClient}
                        currentUser={UserStore.getUser}
                    />

                    <Avatar onClick={( e ) => setSettingsAnchorEl( e.currentTarget )} slotProps={{ img: { crossOrigin: "anonymous" } }} src={`https://acerp.acharyaerptech.in/api/student/studentDetailsFileDownload?fileName=${UserStore.getUser?.image_path}`} sx={{ height: "26px", width: "26px", cursor: "pointer", cursor: "pointer", fontSize: "14px !important" }}>
                        {getNameInitials( ( UserStore.getUser["name"] || UserStore.getUser["user_name"] || "" ) )}
                    </Avatar>

                    <Popper sx={{ zIndex: 100 }} placement='bottom' open={Boolean( settingsAnchorEl )} anchorEl={settingsAnchorEl}>
                        <ClickAwayListener onClickAway={() => setSettingsAnchorEl( null )}>
                            <Card sx={{ marginTop: "10px", marginRight: "20px", border: border[1], borderRadius: "5px" }} elevation={1} >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        padding: "5px"
                                    }}>
                                    <Avatar onClick={( e ) => setSettingsAnchorEl( e.currentTarget )} slotProps={{ img: { crossOrigin: "anonymous" } }} src={`https://acerp.acharyaerptech.in/api/student/studentDetailsFileDownload?fileName=${UserStore.getUser?.image_path}`} sx={{ height: '30px', width: '30px', borderRadius: '5px' }}>{getNameInitials( ( UserStore.getUser["name"] || UserStore.getUser["user_name"] || "" ) )}</Avatar>
                                    <Box>
                                        <Typography
                                            variant='subtitle2'
                                            sx={{
                                                display: "block",
                                                lineHeight: 1.3,
                                                fontSize: "14px",
                                                textTransform: "capitalize"
                                            }}>{UserStore.getUser["name"] || UserStore.getUser["employee_name"] || UserStore.getUser["student_name"] || ""} </Typography>
                                        <Typography
                                            color='textSecondary'
                                            variant='subtitle2'
                                            sx={{
                                                display: "block",
                                                lineHeight: 1.3,
                                                fontSize: "12px",
                                                textTransform: "capitalize"
                                            }}>{UserStore.getUser["AUID"] || UserStore.getUser["empcode"] || UserStore.getUser["user_id"] || ""}</Typography>
                                    </Box>
                                </Box>

                                <Divider />

                                <Box
                                    sx={{
                                        padding: "5px",
                                        justifyContent: "space-between",
                                        display: "flex",
                                        gap: "20px",
                                        alignItems: "center"
                                    }}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center"
                                        }}>
                                        {Icons.default.PaletteOutlined}
                                        <Typography
                                            variant="variant2"
                                            sx={{
                                                width: "max-content",
                                                display: "flex",
                                                fontSize: "12px"
                                            }}>
                                            Theme
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            boxShadow: `inset 0px 0px 3px 0px ${palette.primary.light + "44"}`,
                                            borderRadius: "20px",
                                            bgcolor: palette.contentBg,
                                            padding: "5px",
                                            display: "flex",
                                            gap: "2px",
                                            position: "relative",
                                            alignItems: "center"
                                        }}>

                                        <Box
                                            sx={{
                                                position: "absolute",
                                                bgcolor: palette.background.paper,
                                                borderRadius: "50%",
                                                top: "5px",
                                                bottom: "5px",
                                                left: `${5 + ( themeOptions.findIndex( to => to.value === selectedTheme ) * 30 )}px`,
                                                height: "calc(100% - 10px)",
                                                aspectRatio: "1 / 1",
                                                transition: "all 300ms"
                                            }} />

                                        {themeOptions.map( theme => (
                                            <Tooltip disableInteractive key={theme.value} title={theme.label} >
                                                <IconButton onClick={() => handleThemeChange( theme.value )} size='small' >
                                                    {theme.icon}
                                                </IconButton>
                                            </Tooltip>
                                        ) )}
                                    </Box>
                                </Box>

                                <Divider />

                                <EcosystemPortalCard />

                                <Box sx={{
                                    padding: "5px"
                                }}>
                                    <Link to={ERPDashboardPageURL}  ><Button startIcon={Icons.default.Logout} color='error' size='small' fullWidth variant='contained' disableElevation >Back to ERP</Button></Link>
                                </Box>
                            </Card>
                        </ClickAwayListener>
                    </Popper>
                </Box>}
            </Box>
        </AppBar>
    )
} )

export default Navbar
